import { Column, Row } from "@stenajs-webui/core";
import { FlatButton, stenaMinusCircle } from "@stenajs-webui/elements";
import { find, isEmpty } from "lodash";
import { type FC, useEffect, useRef } from "react";

import { CommonDropdownField } from "../../../../../common-ui/form-fields/CommonDropdownField";
import { CommonNumericTextInputField } from "../../../../../common-ui/form-fields/CommonNumericTextInput";
import { ValidationWrapper } from "../../../../../common-ui/form-fields/ValidationWrapper";
import { BookingGoodsSection } from "../../../../../common-ui/goods/BookingGoodsSection";
import { OnChangeOnBlurTextInput } from "../../../../../common-ui/OnChangeOnBlurTextInput";
import {
  type GoodsItem,
  removeGoodsItem,
} from "../../../../../common/goods/goodsUtils";
import { useGoods } from "../../../../../common/goods/useGoods";
import type { BasicBooking } from "../../../../../common/graphql/fragments/gql/BasicBooking";
import type { BackgroundQueryRef } from "../../../../../common/graphql/types/BackgroundQueryRef";
import { useRecentGoodsTypeCodes } from "../../../../../common/graphql/useRecentBookings";
import { useAppNavigate } from "../../../../../common/hooks/useAppNavigate";
import { useUpdateRegistrationNumberAlertApplied } from "../../../../../common/registrationNumberAlert/hooks/useUpdateRegistrationNumberAlertApplied";
import { trackEvent } from "../../../../../common/tracking/trackerService";
import { useShowAllGoods } from "../../../../../common/update-information/hooks/useShowAllGoods";
import { useUpdateField } from "../../../../../common/update-information/hooks/useUpdateField";
import { useUpdateValidationErrors } from "../../../../../common/update-information/hooks/useUpdateValidationErrors";
import {
  type BookingFields,
  isHidden,
} from "../../../../../common/utils/bookingFields";
import { transformGoodsTypeOption } from "../../../../../common/utils/optionTransformers";
import {
  ColumnId,
  type GoodsTypesQueryQuery,
  type RecentBookingsQueryQuery,
} from "../../../../../gql/graphql";
import { useGoodsTypes } from "../../../../../services/intermodal/hooks/useGoodsTypes";
import { hasDropdownValueChanged } from "../../../../multiple-create/hooks/useDropdownField";
import type { BookingDetailsLocationState } from "../../utils/detailsUtils";

interface GoodsDetailsSectionProps {
  booking: BasicBooking;
  disabled: boolean;
  fieldsToEnable: BookingFields;
  fieldsToShow: BookingFields;
  goodsTypesQueryRef: BackgroundQueryRef<GoodsTypesQueryQuery>;
  recentBookingsQueryRef: BackgroundQueryRef<RecentBookingsQueryQuery>;
}

export const GoodsDetailsSection: FC<GoodsDetailsSectionProps> = ({
  disabled,
  fieldsToEnable,
  fieldsToShow,
  booking: { hazardousGoodsApproved, cargoWeight },
  recentBookingsQueryRef,
  goodsTypesQueryRef,
}) => {
  const { goodsTypes } = useGoodsTypes(goodsTypesQueryRef, {
    includeInvalidGoodsTypes: true,
  });
  const recentGoodsTypeCodes = useRecentGoodsTypeCodes(recentBookingsQueryRef);
  const validationErrors = useUpdateValidationErrors("goods");
  const {
    editedValue: goodsItems = {},
    initialValue: initialGoodsItems = {},
    onChange,
  } = useUpdateField("goods");
  const { editedValue: livestockType } = useUpdateField("livestockType");
  const { showAllGoods, enableShowAllGoods } = useShowAllGoods();

  const isFieldDisabled = (field: ColumnId) =>
    disabled || isHidden(fieldsToEnable, field);
  const goodsDisabled = isFieldDisabled(ColumnId.Goods);

  const arrivalNotepadInfo = useUpdateRegistrationNumberAlertApplied(
    "Details",
    "goodsWeight",
    undefined,
  );

  const {
    getDescriptionInputProps,
    getTypeDropdownProps,
    getWeightInputProps,
    totalGoodsWeight,
    onAddGoodsItem,
  } = useGoods({
    goodsTypes,
    goodsItems,
    onChange,
    trackerCategory: "Details",
    recentGoodsTypeCodes,
    arrivalNotepadInfo,
    livestockType,
  });

  const totalCargoWeight = hazardousGoodsApproved
    ? cargoWeight
    : totalGoodsWeight;

  function getHasGoodsFieldChanged(
    editedGoodsItem: GoodsItem,
    key: "type" | "description" | "weight",
  ) {
    const initialGoodsItem = find(
      initialGoodsItems,
      (item) => item.id === editedGoodsItem.id,
    );

    switch (key) {
      case "type":
        return editedGoodsItem.code !== initialGoodsItem?.code;
      case "description":
        return editedGoodsItem.description !== initialGoodsItem?.description;
      case "weight":
        return editedGoodsItem.weight !== initialGoodsItem?.weight;
      default:
        return false;
    }
  }

  const sectionHeaderRef = useRef<HTMLDivElement>(null);

  const { locationState, removeLocationState } =
    useAppNavigate<BookingDetailsLocationState>();

  useEffect(() => {
    if (locationState.scrollGoodsIntoView) {
      sectionHeaderRef.current?.scrollIntoView({ behavior: "smooth" });
      removeLocationState("scrollGoodsIntoView");
    }
  }, [locationState.scrollGoodsIntoView, removeLocationState]);

  useEffect(() => {
    if (locationState.addEmptyGoodsItem && isEmpty(goodsItems)) {
      onAddGoodsItem();
      removeLocationState("addEmptyGoodsItem");
    }
  }, [
    locationState.addEmptyGoodsItem,
    goodsItems,
    onAddGoodsItem,
    removeLocationState,
  ]);

  if (isHidden(fieldsToShow, ColumnId.Goods)) {
    return null;
  }

  return (
    <Column maxWidth={800}>
      <BookingGoodsSection
        goodsItems={goodsItems}
        onAddGoodsItem={onAddGoodsItem}
        totalWeight={totalCargoWeight}
        disabled={goodsDisabled}
        sectionHeaderRef={sectionHeaderRef}
        showAll={showAllGoods}
        onShowAll={() => {
          trackEvent({ category: "Details", action: "Show all goods" });
          enableShowAllGoods();
        }}
        renderGoodsType={(goodsItem, id) => (
          <ValidationWrapper
            validationError={validationErrors?.[goodsItem.id]?.type}
            renderField={(isValid) => (
              <CommonDropdownField
                id={id}
                disabled={goodsDisabled}
                classNamePrefix={"t_goods_type_select"}
                changed={hasDropdownValueChanged(
                  {
                    editedValue: goodsItem.goodsType,
                    initialValue: find(
                      initialGoodsItems,
                      (g) => g.id === goodsItem.id,
                    )?.goodsType,
                  },
                  transformGoodsTypeOption,
                )}
                {...getTypeDropdownProps(goodsItem, isValid)}
              />
            )}
          />
        )}
        renderGoodsDescription={(goodsItem, id) => (
          <ValidationWrapper
            validationError={validationErrors?.[goodsItem.id]?.description}
            renderField={(isValid) => (
              <OnChangeOnBlurTextInput
                id={id}
                disabled={goodsDisabled}
                changed={getHasGoodsFieldChanged(goodsItem, "description")}
                {...getDescriptionInputProps(goodsItem, isValid)}
              />
            )}
          />
        )}
        renderGoodsWeight={(goodsItem, id) => (
          <ValidationWrapper
            validationError={validationErrors?.[goodsItem.id]?.weight}
            renderField={(isValid) => (
              <Row gap={2} alignItems={"center"}>
                <CommonNumericTextInputField
                  id={id}
                  changed={getHasGoodsFieldChanged(goodsItem, "weight")}
                  disabled={goodsDisabled}
                  {...getWeightInputProps(goodsItem, isValid)}
                />
                {!goodsDisabled && (
                  <FlatButton
                    variant={"danger"}
                    leftIcon={stenaMinusCircle}
                    onClick={() => {
                      trackEvent({
                        category: "Details",
                        action: "Remove goods",
                      });
                      onChange(removeGoodsItem(goodsItems, goodsItem.id));
                    }}
                  />
                )}
              </Row>
            )}
          />
        )}
      />
    </Column>
  );
};
